import React, { useState, useEffect } from "react";
import {
  Popover,
  Box,
  IconButton,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

interface DatePickerProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  value: string;
  onChange: (date: string) => void;
  minDate?: Date;
  maxDate?: Date;
  disablePast?: boolean;
  showFlexibleDates?: boolean;
}

const formatDateToLocalISO = (
  year: number,
  month: number,
  day: number
): string => {
  const y = year;
  const m = String(month + 1).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseLocalDate = (isoDate: string): Date => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const DatePicker: React.FC<DatePickerProps> = ({
  open,
  anchorEl,
  onClose,
  value,
  onChange,
  minDate,
  maxDate,
  disablePast = true,
  showFlexibleDates = true,
}) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  const today = new Date();
  const todayLocal = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const [currentMonth, setCurrentMonth] = useState<Date>(() => {
    return value ? parseLocalDate(value) : todayLocal;
  });

  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  useEffect(() => {
    if (value) {
      setCurrentMonth(parseLocalDate(value));
    }
  }, [value]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  const navigateMonth = (direction: "prev" | "next") => {
    setCurrentMonth((prev) => {
      const newDate = new Date(
        prev.getFullYear(),
        prev.getMonth() + (direction === "prev" ? -1 : 1),
        1
      );
      return newDate;
    });
  };

  const handleDateClick = (year: number, month: number, day: number) => {
    const formattedDate = formatDateToLocalISO(year, month, day);
    onChange(formattedDate);
    onClose();
  };

  const isSelected = (year: number, month: number, day: number) => {
    if (!value) return false;
    const candidate = formatDateToLocalISO(year, month, day);
    return candidate === value;
  };

  const isToday = (year: number, month: number, day: number) => {
    const now = new Date();
    return (
      now.getFullYear() === year &&
      now.getMonth() === month &&
      now.getDate() === day
    );
  };

  const isDateDisabled = (year: number, month: number, day: number) => {
    const date = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (disablePast && date < today) return true;
    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      if (date < min) return true;
    }
    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      if (date > max) return true;
    }
    return false;
  };

  const renderCalendar = (monthOffset: number) => {
    const date = new Date(currentMonth);
    date.setMonth(date.getMonth() + monthOffset);

    const { daysInMonth, startingDayOfWeek, year, month } =
      getDaysInMonth(date);
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<Box key={`empty-${i}`} sx={{ height: { xs: 32, md: 36 } }} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const isoKey = formatDateToLocalISO(year, month, day);
      const selected = isSelected(year, month, day);
      const todayDate = isToday(year, month, day);
      const disabled = isDateDisabled(year, month, day);

      days.push(
        <Button
          key={isoKey}
          onClick={() => !disabled && handleDateClick(year, month, day)}
          onMouseEnter={() => !disabled && setHoveredDate(isoKey)}
          onMouseLeave={() => setHoveredDate(null)}
          disabled={disabled}
          sx={{
            height: { xs: 32, md: 36 },
            minWidth: { xs: 32, md: 36 },
            borderRadius: "50%",
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            fontWeight: 500,
            transition: "all 0.2s",
            color: disabled ? "text.disabled" : "text.primary",
            bgcolor: selected ? "grey.900" : "transparent",
            textDecoration: todayDate && !selected ? "underline" : "none",
            textDecorationThickness: 2,
            transform:
              hoveredDate === isoKey && !selected && !disabled
                ? "scale(1.1)"
                : "scale(1)",
            "&:hover": {
              bgcolor: selected
                ? "grey.900"
                : disabled
                ? "transparent"
                : "grey.100",
            },
            "&.Mui-disabled": {
              color: "text.disabled",
            },
            ...(selected && {
              color: "white",
              "&:hover": {
                bgcolor: "grey.900",
              },
            }),
          }}
        >
          {day}
        </Button>
      );
    }

    return (
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: "100%", lg: 280 } }}>
        <Typography
          variant="subtitle1"
          sx={{
            textAlign: "center",
            fontWeight: 600,
            mb: { xs: 1.5, md: 2 },
            fontSize: { xs: "0.875rem", md: "0.9375rem" },
          }}
        >
          {formatMonthYear(date)}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: { xs: 0.5, md: 0.5 },
            mb: { xs: 1, md: 1 },
          }}
        >
          {["S", "M", "T", "W", "T", "F", "S"].map((day, idx) => (
            <Box
              key={idx}
              sx={{
                height: { xs: 24, md: 28 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: { xs: "0.625rem", md: "0.6875rem" },
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {day}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: { xs: 0.5, md: 0.5 },
          }}
        >
          {days}
        </Box>
      </Box>
    );
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 3, md: 2 },
          p: { xs: 2, md: 2 },
          width: { xs: "90vw", md: isLargeScreen ? 700 : 340 },
          maxWidth: { xs: 360, md: isLargeScreen ? 700 : 380 },
          mt: 1,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          mb: { xs: 2, md: 2 },
          gap: { xs: 1, md: 2 },
        }}
      >
        <IconButton
          onClick={() => navigateMonth("prev")}
          size="small"
          sx={{
            flexShrink: 0,
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 4, lg: 3 },
            flex: 1,
            justifyContent: "center",
          }}
        >
          {isLargeScreen && renderCalendar(0)}
          {renderCalendar(isLargeScreen ? 1 : 0)}
        </Box>

        <IconButton
          onClick={() => navigateMonth("next")}
          size="small"
          sx={{
            flexShrink: 0,
            width: { xs: 28, md: 32 },
            height: { xs: 28, md: 32 },
            "&:hover": { bgcolor: "grey.100" },
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      {showFlexibleDates && (
        <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2, mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 0.75, md: 1 },
              justifyContent: "center",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              sx={{
                px: { xs: 1.25, md: 1.5 },
                py: { xs: 0.5, md: 0.5 },
                fontSize: { xs: "0.688rem", md: "0.75rem" },
                borderWidth: 1.5,
                borderColor: "grey.900",
                color: "grey.900",
                borderRadius: 50,
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  borderWidth: 1.5,
                  bgcolor: "grey.50",
                },
              }}
            >
              Exact
            </Button>
            {["±1", "±2", "±3", "±7", "±14"].map((label) => (
              <Button
                key={label}
                variant="outlined"
                size="small"
                sx={{
                  px: { xs: 1.25, md: 1.5 },
                  py: { xs: 0.5, md: 0.5 },
                  fontSize: { xs: "0.688rem", md: "0.75rem" },
                  borderColor: "grey.400",
                  color: "text.primary",
                  borderRadius: 50,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "grey.900",
                    bgcolor: "grey.50",
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      )}
    </Popover>
  );
};

export default DatePicker;
